<template>
  <!-- 公告弹窗 -->
  <transition name="modal-fade">
    <div 
      v-if="showModal && currentAnnouncement" 
      class="announcement-modal-overlay"
      @click.self="closeModal"
    >
      <div class="announcement-modal-container">
        <!-- 关闭按钮 -->
        <button 
          class="modal-close-btn"
          @click="closeModal"
          aria-label="关闭"
        >
          <i class="fas fa-times"></i>
        </button>
        
        <!-- 公告内容 -->
        <div class="modal-content">
          <!-- 图标和类型标签 -->
          <div class="modal-header">
            <div class="type-icon" :class="`type-${currentAnnouncement.type}`">
              <i :class="getTypeIcon(currentAnnouncement.type)"></i>
            </div>
            <div class="type-badge" :class="`badge-${currentAnnouncement.type}`">
              {{ getTypeLabel(currentAnnouncement.type) }}
            </div>
          </div>
          
          <!-- 标题 -->
          <h2 class="modal-title">
            {{ currentAnnouncement.title }}
          </h2>
          
          <!-- 版本和日期 -->
          <div class="modal-meta">
            <span v-if="currentAnnouncement.version" class="version-tag">
              <i class="fas fa-code-branch"></i>
              v{{ currentAnnouncement.version }}
            </span>
            <span class="date-tag">
              <i class="fas fa-calendar-alt"></i>
              {{ currentAnnouncement.date }}
            </span>
          </div>
          
          <!-- 公告内容 -->
          <div class="modal-body">
            <p class="content-text">{{ currentAnnouncement.content }}</p>
            
            <!-- 详细信息 -->
            <div v-if="currentAnnouncement.details" class="details-section">
              <p class="details-text">{{ currentAnnouncement.details }}</p>
            </div>
            
            <!-- 标签 -->
            <div v-if="currentAnnouncement.tags && currentAnnouncement.tags.length > 0" class="tags-section">
              <span 
                v-for="tag in currentAnnouncement.tags" 
                :key="tag"
                class="tag-item"
              >
                {{ tag }}
              </span>
            </div>
          </div>
          
          <!-- 底部按钮 -->
          <div class="modal-footer">
            <button 
              class="btn-primary-modal"
              @click="goToAnnouncements"
            >
              <i class="fas fa-list-ul"></i>
              查看所有公告
            </button>
            <button 
              class="btn-secondary-modal"
              @click="closeModal"
            >
              我知道了
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import request from '@/services/api'

export default {
  name: 'AnnouncementModal',
  setup() {
    const router = useRouter()
    const showModal = ref(false)
    const currentAnnouncement = ref(null)
    const announcements = ref([])
    
    // 获取localStorage中的公告查看记录
    const getViewedAnnouncements = () => {
      const viewed = localStorage.getItem('viewed_announcements')
      return viewed ? JSON.parse(viewed) : {}
    }
    
    // 更新公告查看记录
    const updateViewedAnnouncement = (announcementId) => {
      const viewed = getViewedAnnouncements()
      
      if (!viewed[announcementId]) {
        viewed[announcementId] = {
          viewCount: 0,
          firstViewedAt: new Date().toISOString(),
          lastViewedAt: null
        }
      }
      
      viewed[announcementId].viewCount += 1
      viewed[announcementId].lastViewedAt = new Date().toISOString()
      
      localStorage.setItem('viewed_announcements', JSON.stringify(viewed))
    }
    
    // 检查公告是否应该显示
    const shouldShowAnnouncement = (announcement) => {
      const viewed = getViewedAnnouncements()
      const record = viewed[announcement.id]
      
      // 如果没有查看记录，应该显示
      if (!record) {
        return true
      }
      
      // 如果查看次数小于1次，应该显示（即只显示一次）
      if (record.viewCount < 1) {
        return true
      }
      
      // 否则不显示
      return false
    }
    
    // 加载公告数据
    const loadAnnouncements = async () => {
      try {
        const response = await request.get('/announcements.json')
        
        if (Array.isArray(response)) {
          announcements.value = response
          
          // 按日期排序，最新的在前
          announcements.value.sort((a, b) => {
            return new Date(b.date) - new Date(a.date)
          })
          
          // 找到第一个应该显示的公告
          const announcementToShow = announcements.value.find(ann => 
            shouldShowAnnouncement(ann)
          )
          
          if (announcementToShow) {
            currentAnnouncement.value = announcementToShow
            showModal.value = true
            
            // 记录查看次数
            updateViewedAnnouncement(announcementToShow.id)
          }
        }
      } catch (error) {
        console.error('加载公告失败:', error)
      }
    }
    
    // 关闭弹窗
    const closeModal = () => {
      showModal.value = false
      
      // 延迟清空数据，等待动画完成
      setTimeout(() => {
        currentAnnouncement.value = null
      }, 300)
    }
    
    // 跳转到公告页面
    const goToAnnouncements = () => {
      closeModal()
      router.push('/announcements')
    }
    
    // 获取类型图标
    const getTypeIcon = (type) => {
      const icons = {
        feature: 'fas fa-star',
        update: 'fas fa-sync-alt',
        fix: 'fas fa-wrench',
        notice: 'fas fa-bullhorn'
      }
      return icons[type] || 'fas fa-info-circle'
    }
    
    // 获取类型标签
    const getTypeLabel = (type) => {
      const labels = {
        feature: '新功能',
        update: '更新',
        fix: '修复',
        notice: '通知'
      }
      return labels[type] || '公告'
    }
    
    onMounted(() => {
      // 延迟500ms加载公告，避免阻塞页面加载
      setTimeout(() => {
        loadAnnouncements()
      }, 500)
    })
    
    return {
      showModal,
      currentAnnouncement,
      closeModal,
      goToAnnouncements,
      getTypeIcon,
      getTypeLabel
    }
  }
}
</script>

<style scoped>
/* 弹窗遮罩 */
.announcement-modal-overlay {
  @apply fixed inset-0 z-50 flex items-center justify-center p-4;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
}

/* 弹窗容器 */
.announcement-modal-container {
  @apply relative w-full max-w-2xl max-h-[85vh] overflow-hidden;
  background: linear-gradient(135deg, rgba(30, 30, 40, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%);
  border-radius: 24px;
  border: 1.5px solid rgba(255, 255, 255, 0.1);
  box-shadow: 
    0 20px 60px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

/* 关闭按钮 */
.modal-close-btn {
  @apply absolute top-6 right-6 z-10
         w-10 h-10 flex items-center justify-center
         rounded-full
         text-gray-400 hover:text-white
         bg-white/5 hover:bg-white/10
         border border-white/10 hover:border-white/20
         transition-all duration-300
         hover:scale-110 hover:rotate-90;
}

/* 弹窗内容 */
.modal-content {
  @apply p-8 overflow-y-auto max-h-[85vh];
}

/* 头部 */
.modal-header {
  @apply flex items-center gap-4 mb-6;
}

.type-icon {
  @apply w-16 h-16 rounded-2xl flex items-center justify-center text-2xl;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.type-icon.type-feature {
  @apply bg-gradient-to-br from-yellow-500 to-orange-500 text-white;
}

.type-icon.type-update {
  @apply bg-gradient-to-br from-blue-500 to-cyan-500 text-white;
}

.type-icon.type-fix {
  @apply bg-gradient-to-br from-green-500 to-emerald-500 text-white;
}

.type-icon.type-notice {
  @apply bg-gradient-to-br from-cyan-500 to-blue-500 text-white;
}

.type-badge {
  @apply px-4 py-2 rounded-full text-sm font-bold;
}

.type-badge.badge-feature {
  @apply bg-yellow-500/20 text-yellow-400 border border-yellow-500/30;
}

.type-badge.badge-update {
  @apply bg-blue-500/20 text-blue-400 border border-blue-500/30;
}

.type-badge.badge-fix {
  @apply bg-green-500/20 text-green-400 border border-green-500/30;
}

.type-badge.badge-notice {
  @apply bg-cyan-500/20 text-cyan-300 border border-cyan-500/30;
}

/* 标题 */
.modal-title {
  @apply text-3xl font-black text-white mb-4;
  background: linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.3;
}

/* 元信息 */
.modal-meta {
  @apply flex items-center gap-4 mb-6 text-sm text-gray-400;
}

.version-tag, .date-tag {
  @apply flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10;
}

.version-tag i, .date-tag i {
  @apply text-xs;
}

/* 内容 */
.modal-body {
  @apply space-y-6;
}

.content-text {
  @apply text-base text-gray-300 leading-relaxed;
}

.details-section {
  @apply p-4 rounded-xl bg-white/5 border border-white/10;
}

.details-text {
  @apply text-sm text-gray-400 leading-relaxed;
}

/* 标签 */
.tags-section {
  @apply flex flex-wrap gap-2;
}

.tag-item {
  @apply px-3 py-1 rounded-lg text-xs font-medium
         bg-primary/10 text-primary border border-primary/20;
}

/* 底部按钮 */
.modal-footer {
  @apply flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-white/10;
}

.btn-primary-modal {
  @apply flex-1 px-6 py-3.5 rounded-xl
         bg-gradient-to-r from-primary to-secondary
         text-white font-bold text-sm
         flex items-center justify-center gap-2
         transition-all duration-300
         hover:scale-105;
  box-shadow: 0 4px 12px rgba(255, 0, 80, 0.3);
}

.btn-primary-modal:hover {
  box-shadow: 0 8px 24px rgba(255, 0, 80, 0.5), 0 4px 12px rgba(0, 229, 255, 0.3);
}

.btn-secondary-modal {
  @apply flex-1 px-6 py-3.5 rounded-xl
         bg-white/5 hover:bg-white/10
         border border-white/10 hover:border-white/20
         text-gray-300 hover:text-white font-semibold text-sm
         transition-all duration-300
         hover:scale-105;
}

/* 动画 */
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active .announcement-modal-container,
.modal-fade-leave-active .announcement-modal-container {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .announcement-modal-container {
  transform: scale(0.9) translateY(-20px);
  opacity: 0;
}

.modal-fade-leave-to .announcement-modal-container {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}

/* 滚动条样式 */
.modal-content::-webkit-scrollbar {
  width: 6px;
}

.modal-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.modal-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>

